import { useState, useEffect, Fragment } from 'react';
import { supabase } from '../../lib/supabase';
import { BUILDINGS } from '../../lib/workerData';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

// Deliberately not orange for special_event — that color is reserved
// entirely for COVERAGE_COLORS.unassigned below, so a manager scanning the
// page never has to wonder whether an orange tag means "nobody's covering
// this" or just "this is a one-off event."
// Also deliberately not the navy used for "this is selected / this is the
// primary action" (week tabs, view toggle, Add/Save buttons) — these three
// are category tags, not selection state, so each gets its own color that
// isn't doing double duty as chrome. Contrast-checked at 11px bold on white:
// rose #9D174D ~7.9:1, purple #6B21A8 ~8.7:1, blue #1D4ED8 ~6.7:1.
const EVENT_TYPE_LABELS = {
  // No colored background wash here on purpose (Lillian's call, live review) —
  // a rare, occasional tag doesn't need its own color fill on top of the
  // page's real color language (coverage status); the rose text alone still
  // identifies it clearly against the same neutral chip background used
  // elsewhere on this page.
  special_event: { label: 'Special Event', color: '#9D174D', bg: 'rgba(91,107,114,0.06)' },
  rental: { label: 'Weekly Clean', color: '#1D4ED8', bg: 'rgba(29,78,216,0.08)' },
  heavy_rental_week: { label: 'Heavy Rental Week', color: '#6B21A8', bg: 'rgba(107,33,168,0.08)' },
};

// Day and night are told apart by a symbol and a word, deliberately not by
// color. Color on this page means one thing only — whether the shift has
// somebody on it — and day used to be painted the same green that the legend
// calls "fully assigned", so a staffed night shift read as a problem.
const SHIFT_ACCENTS = {
  day:   { icon: '☀', label: 'DAY' },
  night: { icon: '☾', label: 'NIGHT' },
};

// Same neutral chrome tint used for CommandCentral's INFO severity — a calm
// grey-on-paper that reads as "structural label", not a status.
const NEUTRAL_HEAD = 'rgba(91,107,114,0.06)';

// An empty slot used to read "— Worker 2 —", which looks like the name of a
// person rather than an empty box. The first slot now matches the wording every
// other building uses, and the rest say outright that they're optional — most
// shifts here run with one person.
function emptySlotLabel(pos) {
  return pos === 0 ? '— Assign —' : `+ Add ${pos + 1}${['nd', 'rd'][pos - 1] || 'th'} person`;
}

// The one color language on this page: is somebody on this shift or not.
// solid/soft/border are for fills, tints and borders (3:1 contrast is enough
// there). Small bold status TEXT is a separate concern below — the amber
// fill (#B5730A) drops under 4.5:1 at text sizes, same issue already found
// and fixed on Command Central.
// "Below minimum staff" and "still needs someone" used to be two separate
// colors — Lillian's call after looking at it live: if you have to stop and
// think about whether they mean something operationally different, they're
// not earning the extra color. Both keys stay (other logic below still
// distinguishes them for the coverage count/summary text), but they now
// render identically: green means fully covered, anything else means it needs
// a look, one single "needs attention" treatment.
const COVERAGE_COLORS = {
  assigned:    { solid: '#2F7A4F', soft: 'rgba(47,122,79,0.12)',  border: 'rgba(47,122,79,0.4)' },
  understaffed:{ solid: '#B33A2E', soft: 'rgba(179,58,46,0.10)', border: 'rgba(179,58,46,0.4)' },
  unassigned:  { solid: '#B33A2E', soft: 'rgba(179,58,46,0.10)', border: 'rgba(179,58,46,0.4)' },
};

// Text-safe variants for the same three meanings. Checked at 11-12px bold on
// white/paper: green #2F7A4F ~5.2:1 and red #B33A2E ~5.3-5.9:1 both clear
// 4.5:1 as-is, so they're reused directly. Amber does not — #B5730A is only
// ~3.9:1 at that size, so status TEXT (legend labels, "N to assign") uses
// #96600A instead, which measures ~5.3:1.
const COVERAGE_TEXT = {
  assigned: '#2F7A4F',
  understaffed: '#B33A2E',
  unassigned: '#B33A2E',
};

function getRentalBuilding(id) {
  return BUILDINGS.find(b => b.id === id);
}

const DAY_LABELS = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

// "Aug 17" for a given weekday within the selected week — mirrors the Town
// Portal, which shows the real date beside every day so there's no guessing
// which calendar day a shift actually falls on.
function dayDate(weekStart, dayName) {
  const idx = ROSTER_DAYS.indexOf(dayName);
  if (!weekStart || idx < 0) return '';
  const d = new Date(weekStart + 'T12:00:00');
  d.setDate(d.getDate() + idx);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (h === 0) return '12:00 AM';
  const hour = h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

// '17:30:00' -> '5:30p', '17:00:00' -> '5p'
function compactTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const suffix = h >= 12 && h < 24 ? 'p' : 'a';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, '0')}${suffix}`;
}

// The roster card used to state PDIC's hours as literal text, while the crew's
// phones read them from the buildings table. The two disagreed for months and
// nothing surfaced it, because no screen displayed the stored value. These read
// from the same row the worker app does, so they can't drift again. The literals
// below are only a fallback for when the row can't be loaded.
const ROSTER_HOURS_FALLBACK = {
  day: { long: '8:30 AM – 5 PM', short: 'DAY · 8:30a–5p' },
  night: { long: '5:30 PM – 2:30 AM', short: 'NIGHT · 5:30p–2:30a' },
};

function rosterHoursPair(hours, kind) {
  if (!hours) return [null, null];
  return kind === 'day'
    ? [hours.day_start, hours.day_end]
    : [hours.night_start, hours.night_end];
}

function rosterHoursLong(hours, kind) {
  const [from, to] = rosterHoursPair(hours, kind);
  if (!from || !to) return ROSTER_HOURS_FALLBACK[kind].long;
  return `${formatTime(from)} – ${formatTime(to)}`;
}

function rosterHoursShort(hours, kind) {
  const [from, to] = rosterHoursPair(hours, kind);
  if (!from || !to) return ROSTER_HOURS_FALLBACK[kind].short;
  return `${kind === 'day' ? 'DAY' : 'NIGHT'} · ${compactTime(from)}–${compactTime(to)}`;
}

// Label for a single cleaning window. Windows with a real time already
// disambiguate themselves (4pm vs 8:30pm); windows without one (type
// "anytime"/"tbd") look identical when a day has more than one, so those
// get numbered ("Shift 1"/"Shift 2") — same idea as PDIC/RPYCC's numbered
// Worker 1/Worker 2 slots, just for buildings whose visits aren't named
// day/night.
// What the Town wrote against a building's cleaning slots this week — set the
// alarm, lock the gates, one cleaning per week if nothing is booked. The card
// renders each slot as a time and drops everything else, so until Aug 2026
// these were stored every week and shown to nobody.
function uniqueWindowNotes(row) {
  return Array.from(new Set((row?.windows || []).map(w => w.note).filter(Boolean)));
}

function windowLabel(w, indexInDay, totalInDay) {
  const wt = w.type || 'time';
  if (wt === 'time') return `${formatTime(w.start)}${w.end ? `–${formatTime(w.end)}` : ''}`;
  const base = wt === 'anytime' ? 'Anytime' : 'TC to Schedule';
  return totalInDay > 1 ? `${base} — Shift ${indexInDay + 1}` : base;
}

function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getUpcomingMondays(count = 8) {
  const mondays = [];
  const today = new Date();
  const day = today.getDay();
  const mon = new Date(today);
  mon.setDate(today.getDate() + (day === 0 ? -6 : 1 - day));
  for (let i = 0; i < count; i++) {
    // Local date, not toISOString() — avoids the UTC-rollover bug that can
    // push the evening's "today" to tomorrow and misalign the whole
    // Monday-anchored week picker.
    mondays.push(localDateStr(mon));
    mon.setDate(mon.getDate() + 7);
  }
  return mondays;
}

function formatWeekLabel(mondayStr) {
  const mon = new Date(mondayStr + 'T12:00:00');
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${mon.toLocaleDateString('en-CA', opts)} – ${sun.toLocaleDateString('en-CA', { ...opts, year: 'numeric' })}`;
}

function prevMonday(mondayStr) {
  const d = new Date(mondayStr + 'T12:00:00');
  d.setDate(d.getDate() - 7);
  return localDateStr(d);
}

// Does this building still have work outstanding — meaning any cleaning slot
// with nobody against it, not merely "nobody at all". A building where six of
// seven visits are covered still needs someone, and counting it as done is how
// the seventh gets missed.
//
// E/F are excluded on purpose: they're staffed by the standing roster rather
// than week by week, so they'd otherwise show as permanently outstanding work
// that nobody can ever clear.
function rentalNeedsStaff(r) {
  if (r.building_id === 'E' || r.building_id === 'F') return false;
  const windows = r.windows || [];
  if (windows.length === 0) return (r.assigned_workers || []).length === 0;
  return windows.some(w => !w?.worker_id);
}

function countBuildingsNeedingStaff(rows) {
  return (rows || []).filter(rentalNeedsStaff).length;
}

// Per-building shift/window coverage count, for the accordion's collapsed
// summary row. Same underlying signal rentalNeedsStaff already reads
// (windows[].worker_id, or the whole-week assignment pool for a building with
// no discrete windows) — just tallied per building instead of reduced to a
// single yes/no.
function rentalCoverageSummary(r) {
  const windows = r.windows || [];
  if (windows.length === 0) {
    const has = (r.assigned_workers || []).length > 0;
    return { filled: has ? 1 : 0, total: 1 };
  }
  return { filled: windows.filter(w => w?.worker_id).length, total: windows.length };
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatDateRange(start, end) {
  if (start === end) return formatDate(start);
  return `${formatDate(start)} – ${formatDate(end)}`;
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T12:00:00');
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

const ROSTER_DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

// The arena and the youth centre share an address but are separate buildings
// with separate standing rosters. They shared one grid until Aug 2026, which
// couldn't describe a Wednesday night with Gary and Mahmoud on the arena and
// April on the youth centre — three people in a grid holding two — so April's
// real Wed–Sun pattern had been squeezed down to weekends to make it fit.
const ROSTER_GROUPS = [
  { key: 'PDIC',  buildingId: 'E', code: 'E', name: 'Paradise Double Ice Complex', slots: 3 },
  { key: 'RPYCC', buildingId: 'F', code: 'F', name: 'Rotary Paradise Youth & Community Centre', slots: 2 },
];
const ROSTER_GROUP_KEYS = ROSTER_GROUPS.map(g => g.key);

// E and F show as one grid: five slots per shift, the first three stored on the
// arena roster (PDIC) and the last two on the youth centre roster (RPYCC), so
// saved assignments and everything that reads them stay as they were.
const EF_GROUP = { key: 'EF', buildingId: 'E', code: 'E + F', name: 'Paradise Double Ice Complex & Rotary Paradise Youth & Community Centre', slots: 5 };
const EF_ARENA_SLOTS = 3;
function slotTarget(group, pos) {
  if (group.key !== 'EF') return { key: group.key, position: pos };
  return pos < EF_ARENA_SLOTS ? { key: 'PDIC', position: pos } : { key: 'RPYCC', position: pos - EF_ARENA_SLOTS };
}

// Seeds shift_assignments only when the table is empty for a building.
//
// The arena's days are where they've always been. Nobody has said which of the
// two buildings Sal, Omar and Elizabeth actually clean, so they stay where they
// were rather than being moved on a guess — if some of them are really on the
// youth centre, Fabian moves them and it sticks.
//
// The youth centre's days are deliberately blank. The Town requires a minimum
// of one janitorial staff there 8am–10pm, so those blanks are real outstanding
// work and will show as such, rather than being quietly filled by whoever
// seemed likely.
const OFFICIAL_ROSTER = {
  PDIC: {
    monday:    { day: ['Sal'],               night: ['Gary', 'Mahmoud'] },
    tuesday:   { day: ['Sal'],               night: ['Gary', 'Mahmoud'] },
    wednesday: { day: ['Sal'],               night: ['Gary', 'Mahmoud'] },
    thursday:  { day: ['Sal'],               night: ['Gary', 'Mahmoud'] },
    friday:    { day: ['Omar'],              night: ['Gary', 'Mahmoud'] },
    saturday:  { day: ['Omar', 'Elizabeth'], night: [] },
    sunday:    { day: ['Omar', 'Elizabeth'], night: [] },
  },
  RPYCC: {
    monday:    { day: [], night: [] },
    tuesday:   { day: [], night: [] },
    wednesday: { day: [], night: ['April'] },
    thursday:  { day: [], night: ['April'] },
    friday:    { day: [], night: ['April'] },
    saturday:  { day: [], night: ['April'] },
    sunday:    { day: [], night: ['April'] },
  },
};

// scheduled_events only needs to cover "current-ish" events for this
// screen (everything shown here filters on end_date >= today anyway) —
// bounding the query keeps it from growing unbounded as the table
// accumulates years of history.
const EVENTS_LOOKBACK_DAYS = 30;
function eventsCutoffStr() {
  const d = new Date();
  d.setDate(d.getDate() - EVENTS_LOOKBACK_DAYS);
  return localDateStr(d);
}

function emptyRoster() {
  const week = {};
  for (const g of ROSTER_GROUPS) {
    week[g.key] = {};
    for (const d of ROSTER_DAYS) week[g.key][d] = { day: [], night: [] };
  }
  return week;
}

function buildRosterFromRows(rows, profiles) {
  const loaded = emptyRoster();
  for (const r of rows) {
    const group = loaded[r.building_group];
    if (!group || !group[r.day_of_week] || !group[r.day_of_week][r.shift_type]) continue;
    const w = profiles.find(x => x.id === r.worker_id);
    const nm = w?.full_name || r.worker_name || 'Worker';
    group[r.day_of_week][r.shift_type].push({ position: r.position, worker_id: r.worker_id, name: nm });
  }
  for (const key of ROSTER_GROUP_KEYS) {
    for (const d of ROSTER_DAYS) {
      for (const type of ['day', 'night']) loaded[key][d][type].sort((a, b) => a.position - b.position);
    }
  }
  return loaded;
}

export default function ScheduleManager() {
  const isWide = useMediaQuery('(min-width: 760px)');
  const [events, setEvents] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [view, setView] = useState('rentals'); // events | rentals
  const [rentalWeek, setRentalWeek] = useState(() => getUpcomingMondays(1)[0]);
  const [rentalSchedules, setRentalSchedules] = useState([]);
  const [loadingRentals, setLoadingRentals] = useState(false);
  const [loadedRentalWeek, setLoadedRentalWeek] = useState(null);
  const [shiftAssignments, setShiftAssignments] = useState(emptyRoster);
  const [rosterError, setRosterError] = useState(null);
  // PDIC and RPYCC share one set of hours, so building E speaks for the card.
  const [rosterHours, setRosterHours] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [copyingLastWeek, setCopyingLastWeek] = useState(false);
  const [copyResult, setCopyResult] = useState(null);
  // Outstanding-work counts for the two weeks offered as buttons. Only needed
  // for the week that isn't currently open — the open one is counted live from
  // rentalSchedules, so the number drops as the manager assigns people.
  const [weekCounts, setWeekCounts] = useState({});
  const [onlyNeeding, setOnlyNeeding] = useState(false);
  // Which building cards are expanded, keyed by building_id ('A', 'B', ...,
  // 'E' for PDIC, 'F' for RPYCC). Empty by default — every building starts
  // collapsed to a name + at-a-glance coverage summary, per Lillian's request
  // after seeing the whole page render as one long fully-expanded list.
  // A Set (not a single "which one's open" id) so multiple buildings can be
  // expanded at once without collapsing each other.
  const [expandedBuildings, setExpandedBuildings] = useState(() => new Set());

  function defaultForm() {
    return {
      event_type: 'special_event',
      name: '',
      location: '',
      building_id: '',
      start_date: '',
      end_date: '',
      hourly_billing: false,
      notes: '',
      assigned_workers: [],
      assigned_worker_names: [],
    };
  }

  // Re-fetch both standing rosters straight from the DB. Used after a
  // shift-assignment write fails, so the UI reflects what's actually saved
  // instead of an optimistic change that never persisted.
  async function reloadRoster() {
    const [assignRes, profilesRes] = await Promise.all([
      supabase.from('shift_assignments').select('*').in('building_group', ROSTER_GROUP_KEYS),
      supabase.from('profiles').select('id, full_name').in('role', ['worker', 'supervisor']),
    ]);
    if (assignRes.error || profilesRes.error) {
      setRosterError((assignRes.error || profilesRes.error).message);
      return;
    }
    setShiftAssignments(buildRosterFromRows(assignRes.data || [], profilesRes.data || []));
  }

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const [eventsRes, workersRes, allProfiles, assignRes, hoursRes] = await Promise.all([
        supabase.from('scheduled_events').select('*').gte('end_date', eventsCutoffStr()).order('start_date', { ascending: true }),
        // is_test excluded here only — this list populates the assignment
        // dropdowns, and the test account sat one row above the real Omar.
        // The queries that resolve a saved worker_id back to a name are left
        // unfiltered, so an already-assigned test worker still shows by name
        // rather than silently going blank.
        supabase.from('profiles').select('id, full_name, role, shift, assigned_buildings').in('role', ['worker', 'supervisor']).eq('is_test', false),
        supabase.from('profiles').select('id, full_name, shift, weekly_shifts').in('role', ['worker', 'supervisor']),
        supabase.from('shift_assignments').select('*'),
        supabase.from('buildings').select('building_id, day_start, day_end, night_start, night_end').in('building_id', ROSTER_GROUPS.map(g => g.buildingId)),
      ]);
      if (eventsRes.data) setEvents(eventsRes.data);
      if (workersRes.data) setWorkers(workersRes.data);
      if (hoursRes.data) {
        setRosterHours(Object.fromEntries(hoursRes.data.map(h => [h.building_id, h])));
      }
      // Standing rosters for the arena and the youth centre. Load from
      // shift_assignments; seed a building from the official roster only if it
      // has no rows at all, so seeding the youth centre for the first time
      // can't disturb the arena's existing assignments.
      if (allProfiles.data) {
        const existing = (assignRes.data || []).filter(r => ROSTER_GROUP_KEYS.includes(r.building_group));
        const seeded = buildRosterFromRows(existing, allProfiles.data);
        const seedFailures = [];
        const missingNames = new Set();

        for (const g of ROSTER_GROUPS) {
          if (existing.some(r => r.building_group === g.key)) continue;
          for (const d of ROSTER_DAYS) {
            for (const type of ['day', 'night']) {
              let position = 0;
              for (const nm of OFFICIAL_ROSTER[g.key][d][type] || []) {
                const w = allProfiles.data.find(x => x.full_name === nm);
                if (!w) {
                  // Which building/day/shift doesn't matter to whoever reads this —
                  // the same person is usually missing across several slots, and a
                  // separate line per slot just buries the one thing worth acting on.
                  missingNames.add(nm);
                  continue;
                }
                const pos = position++;
                seeded[g.key][d][type].push({ position: pos, worker_id: w.id, name: w.full_name });
                // eslint-disable-next-line no-await-in-loop
                const { error } = await supabase.from('shift_assignments').insert({
                  building_group: g.key, day_of_week: d, shift_type: type,
                  position: pos, worker_id: w.id, worker_name: w.full_name,
                });
                if (error) seedFailures.push(`${nm} (${g.key} ${d} ${type}): ${error.message}`);
              }
            }
          }
        }

        setShiftAssignments(seeded);
        const errorParts = [];
        if (missingNames.size) {
          errorParts.push(
            `${missingNames.size} name(s) in the standing roster have no profile yet — add them or edit the roster: ${[...missingNames].join(', ')}`
          );
        }
        if (seedFailures.length) {
          errorParts.push(
            `${seedFailures.length} roster slot(s) failed to save automatically — reopen and re-set them manually: ${seedFailures.join('; ')}`
          );
        }
        if (errorParts.length) setRosterError(errorParts.join(' '));
      }
      setLoading(false);
    }
    loadData();
    loadRentalSchedules(rentalWeek);
    loadWeekCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadWeekCounts() {
    const weeks = getUpcomingMondays(2);
    const { data } = await supabase
      .from('rental_schedules')
      .select('building_id, week_start, assigned_workers, windows')
      .in('week_start', weeks);
    const counts = {};
    for (const wk of weeks) {
      const rows = (data || []).filter(r => r.week_start === wk);
      counts[wk] = { submitted: rows.length, needing: countBuildingsNeedingStaff(rows) };
    }
    setWeekCounts(counts);
  }

  // The open week is counted from what's on screen so the button updates as
  // workers are assigned; other weeks come from the fetched snapshot.
  function weekSummary(week) {
    if (week === loadedRentalWeek) {
      return { submitted: rentalSchedules.length, needing: countBuildingsNeedingStaff(rentalSchedules) };
    }
    return weekCounts[week] || null;
  }

  function selectWeek(week) {
    setRentalWeek(week);
    setCopyResult(null);
    setOnlyNeeding(false);
    loadRentalSchedules(week);
  }

  // Toggles one building's card open/closed independently of every other
  // building's state.
  function toggleBuildingExpanded(buildingId) {
    setExpandedBuildings(prev => {
      const next = new Set(prev);
      if (next.has(buildingId)) next.delete(buildingId);
      else next.add(buildingId);
      return next;
    });
  }

  const needingCount = countBuildingsNeedingStaff(rentalSchedules);

  async function handleShiftAssign(group, day, type, position, workerId) {
    setRosterError(null);
    const worker = workers.find(x => x.id === workerId);

    // Update local state immediately for snappy UX
    setShiftAssignments(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const list = next[group][day][type];
      const existingIdx = list.findIndex(x => x.position === position);
      if (workerId) {
        const entry = { position, worker_id: workerId, name: worker?.full_name || 'Worker' };
        if (existingIdx >= 0) list[existingIdx] = entry;
        else list.push(entry);
      } else if (existingIdx >= 0) {
        list.splice(existingIdx, 1);
      }
      next[group][day][type] = list;
      return next;
    });

    // Persist: upsert the row for this day/type/position, or delete if cleared.
    // Any failure here reverts the UI to what's actually saved in the DB —
    // an optimistic update with no error check previously left the UI
    // showing "saved" changes that never persisted.
    const { data: rows, error: selectError } = await supabase
      .from('shift_assignments')
      .select('*')
      .eq('building_group', group)
      .eq('day_of_week', day)
      .eq('shift_type', type)
      .eq('position', position);

    let writeError = selectError;
    if (!writeError) {
      if (workerId) {
        if (rows && rows.length) {
          const { error } = await supabase.from('shift_assignments')
            .update({ worker_id: workerId, worker_name: worker?.full_name || null })
            .eq('id', rows[0].id);
          writeError = error;
        } else {
          const { error } = await supabase.from('shift_assignments').insert({
            building_group: group, day_of_week: day, shift_type: type,
            position, worker_id: workerId, worker_name: worker?.full_name || null,
          });
          writeError = error;
        }
      } else if (rows && rows.length) {
        const { error } = await supabase.from('shift_assignments').delete().eq('id', rows[0].id);
        writeError = error;
      }
    }

    if (writeError) {
      setRosterError(`Could not save that shift change — reverted to the last saved roster. (${writeError.message})`);
      await reloadRoster();
    }
  }

  async function reloadData() {
    const [eventsRes, workersRes] = await Promise.all([
      supabase.from('scheduled_events').select('*').gte('end_date', eventsCutoffStr()).order('start_date', { ascending: true }),
      supabase.from('profiles').select('id, full_name, role, shift, assigned_buildings').in('role', ['worker', 'supervisor']).eq('is_test', false),
    ]);
    if (eventsRes.data) setEvents(eventsRes.data);
    if (workersRes.data) setWorkers(workersRes.data);
  }

  async function loadRentalSchedules(week, { force = false } = {}) {
    // Skip the refetch if this week's data is already loaded — e.g. the
    // "Rentals" tab button previously refetched on every click even when
    // the selected week hadn't changed since the last load.
    if (!force && week === loadedRentalWeek) return;
    setLoadingRentals(true);
    const { data } = await supabase
      .from('rental_schedules')
      .select('*')
      .eq('week_start', week)
      .order('building_id', { ascending: true });
    if (data) setRentalSchedules(data);
    setLoadedRentalWeek(week);
    setLoadingRentals(false);
  }

  // Pins a specific worker to one cleaning window (e.g. the 4pm visit vs.
  // the 8:30pm visit on the same day) instead of just the whole-week pool —
  // a building cleaned twice a day can need two different people. Assigning
  // someone to a window also adds them to the overall weekly pool (so they
  // still show up for WorkerApp visibility / min-staff counts) without
  // removing anyone already chip-assigned there.
  // Applies a windows/pool update to one rental both locally (instant,
  // no flicker) and in the DB. Previously this called loadRentalSchedules()
  // after every save, which flips loadingRentals on/off and briefly replaces
  // the whole (long) building list with a one-line "Loading..." placeholder
  // — collapsing the page's height and resetting scroll to the top while
  // you're mid-way through assigning workers. Updating local state directly
  // avoids that unmount entirely; a full reload only happens if the save
  // actually fails, to resync with what's really in the DB.
  async function saveRentalWindows(rental, newWindows, poolIds, poolNames) {
    const newAssignedWorkers = Array.from(poolIds);
    const newAssignedWorkerNames = Array.from(poolNames);
    setRentalSchedules(prev => prev.map(r => r.id === rental.id
      ? { ...r, windows: newWindows, assigned_workers: newAssignedWorkers, assigned_worker_names: newAssignedWorkerNames }
      : r));
    const { error } = await supabase
      .from('rental_schedules')
      .update({
        windows: newWindows,
        assigned_workers: newAssignedWorkers,
        assigned_worker_names: newAssignedWorkerNames,
        updated_at: new Date().toISOString(),
      })
      .eq('id', rental.id);
    if (error) {
      setSaveError(error.message);
      await loadRentalSchedules(rentalWeek, { force: true });
    }
  }

  async function assignRentalWindowWorker(rental, windowIndex, workerId) {
    const worker = workerId ? workers.find(w => w.id === workerId) : null;
    const newWindows = (rental.windows || []).map((w, i) =>
      i === windowIndex
        ? { ...w, worker_id: worker?.id || undefined, worker_name: worker?.full_name || undefined }
        : w
    );
    const poolIds = new Set(rental.assigned_workers || []);
    const poolNames = new Set(rental.assigned_worker_names || []);
    if (worker) { poolIds.add(worker.id); poolNames.add(worker.full_name); }
    await saveRentalWindows(rental, newWindows, poolIds, poolNames);
  }

  // Pin the same worker to every time slot for the week in one go — most
  // buildings are cleaned by the same person every day, so setting each
  // day's dropdown individually is unnecessary busywork.
  async function assignAllWindowsWorker(rental, workerId) {
    const worker = workerId ? workers.find(w => w.id === workerId) : null;
    const newWindows = (rental.windows || []).map(w => ({
      ...w, worker_id: worker?.id || undefined, worker_name: worker?.full_name || undefined,
    }));
    const poolIds = new Set(rental.assigned_workers || []);
    const poolNames = new Set(rental.assigned_worker_names || []);
    if (worker) { poolIds.add(worker.id); poolNames.add(worker.full_name); }
    await saveRentalWindows(rental, newWindows, poolIds, poolNames);
  }

  // Fills in still-blank worker slots for this week using whoever worked the
  // matching day/shift-position last week — never overwrites a slot someone
  // has already assigned this week. Matches by position within the day
  // (1st visit, 2nd visit) rather than exact time, since the Town's times
  // can shift slightly week to week while the same person keeps the route.
  // PDIC/RPYCC (E/F) are skipped — that roster already carries forward
  // permanently via shift_assignments, it isn't week-specific.
  async function copyPreviousWeekAssignments() {
    setCopyingLastWeek(true);
    setCopyResult(null);
    setSaveError(null);
    try {
      const prevWeekDate = new Date(rentalWeek + 'T12:00:00');
      prevWeekDate.setDate(prevWeekDate.getDate() - 7);
      const prevWeek = localDateStr(prevWeekDate);

      const { data: prevRows, error } = await supabase
        .from('rental_schedules')
        .select('building_id, windows')
        .eq('week_start', prevWeek);
      if (error) { setSaveError(error.message); return; }
      if (!prevRows || prevRows.length === 0) {
        setCopyResult(`No schedule found for the week of ${formatWeekLabel(prevWeek)} — nothing to copy.`);
        return;
      }
      const prevByBuilding = new Map(prevRows.map(r => [r.building_id, r]));

      let filledCount = 0;
      let buildingsTouched = 0;
      for (const rental of rentalSchedules) {
        if (rental.building_id === 'E' || rental.building_id === 'F') continue;
        const prev = prevByBuilding.get(rental.building_id);
        if (!prev) continue;

        const byDayPrev = {};
        for (const w of (prev.windows || [])) {
          (byDayPrev[w.day] = byDayPrev[w.day] || []).push(w);
        }

        const seenPerDay = {};
        const newWindows = (rental.windows || []).map(w => {
          const idx = seenPerDay[w.day] || 0;
          seenPerDay[w.day] = idx + 1;
          if (w.worker_id) return w;
          const match = (byDayPrev[w.day] || [])[idx];
          if (match?.worker_id && workers.some(wk => wk.id === match.worker_id)) {
            return { ...w, worker_id: match.worker_id, worker_name: match.worker_name };
          }
          return w;
        });
        const filledHere = newWindows.filter((w, i) => w.worker_id && !(rental.windows || [])[i]?.worker_id).length;

        if (filledHere > 0) {
          filledCount += filledHere;
          buildingsTouched += 1;
          const poolIds = new Set(rental.assigned_workers || []);
          const poolNames = new Set(rental.assigned_worker_names || []);
          newWindows.forEach(w => { if (w.worker_id) { poolIds.add(w.worker_id); poolNames.add(w.worker_name); } });
          // eslint-disable-next-line no-await-in-loop
          await saveRentalWindows(rental, newWindows, poolIds, poolNames);
        }
      }

      setCopyResult(filledCount > 0
        ? `Filled ${filledCount} open slot${filledCount === 1 ? '' : 's'} across ${buildingsTouched} building${buildingsTouched === 1 ? '' : 's'} from the week of ${formatWeekLabel(prevWeek)}.`
        : `Nothing to fill — every slot already has a worker assigned this week, or last week had no matching assignments to copy.`);
    } finally {
      setCopyingLastWeek(false);
    }
  }

  // Get upcoming special events (today and forward)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const specialEvents = events.filter(e =>
    e.event_type === 'special_event' || e.event_type === 'heavy_rental_week'
  );

  const upcomingSpecial = specialEvents.filter(e => {
    const end = new Date(e.end_date + 'T12:00:00');
    return end >= today;
  });

  function toggleWorker(worker) {
    const alreadyAssigned = form.assigned_workers.includes(worker.id);
    setForm(prev => ({
      ...prev,
      assigned_workers: alreadyAssigned
        ? prev.assigned_workers.filter(id => id !== worker.id)
        : [...prev.assigned_workers, worker.id],
      assigned_worker_names: alreadyAssigned
        ? prev.assigned_worker_names.filter(n => n !== worker.full_name)
        : [...prev.assigned_worker_names, worker.full_name],
    }));
  }

  function openAdd() {
    setForm(defaultForm());
    setEditingEvent(null);
    setSaveError(null);
    setShowAddForm(true);
  }

  function openEdit(event) {
    setSaveError(null);
    setForm({
      event_type: event.event_type,
      name: event.name,
      location: event.location,
      building_id: event.building_id || '',
      start_date: event.start_date,
      end_date: event.end_date,
      hourly_billing: event.hourly_billing,
      notes: event.notes || '',
      assigned_workers: event.assigned_workers || [],
      assigned_worker_names: event.assigned_worker_names || [],
    });
    setEditingEvent(event);
    setShowAddForm(true);
  }

  async function handleSave() {
    if (!form.name || !form.location || !form.start_date) return;
    setSaving(true);
    setSaveError(null);
    const payload = {
      ...form,
      end_date: form.end_date || form.start_date,
      is_multi_day: form.end_date && form.end_date !== form.start_date,
      hourly_billing: form.event_type === 'special_event',
    };
    const { error } = editingEvent
      ? await supabase.from('scheduled_events').update(payload).eq('id', editingEvent.id)
      : await supabase.from('scheduled_events').insert([payload]);
    if (error) {
      setSaving(false);
      setSaveError(error.message);
      return;
    }
    await reloadData();
    setShowAddForm(false);
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this entry?')) return;
    const { error } = await supabase.from('scheduled_events').delete().eq('id', id);
    if (error) {
      alert(`Could not delete this entry: ${error.message}`);
      return;
    }
    await reloadData();
  }

  if (loading) return <div style={s.loading}>Loading schedule...</div>;

  // A day/night slot counts as covered once one person is on it. The roster
  // allows two, but the second is a spare, not a requirement.
  function rosterSlotFilled(group, day, shift) {
    if (group === 'EF') return ROSTER_GROUPS.some(g => rosterSlotFilled(g.key, day, shift));
    return (shiftAssignments[group]?.[day]?.[shift] || []).some(x => x.worker_id);
  }

  function slotAssigned(group, day, shift, pos) {
    const t = slotTarget(group, pos);
    return (shiftAssignments[t.key]?.[day]?.[shift] || []).find(x => x.position === t.position);
  }

  function assignSlot(group, day, shift, pos, workerId) {
    const t = slotTarget(group, pos);
    // E and F are one building, so a person can hold only one slot per shift.
    const alreadyOn = workerId && ROSTER_GROUPS.some(g => (shiftAssignments[g.key]?.[day]?.[shift] || [])
      .some(x => x.worker_id === workerId && !(g.key === t.key && x.position === t.position)));
    if (alreadyOn) {
      alert('That person is already on this shift. Pick someone else, or clear their other slot first.');
      return;
    }
    return handleShiftAssign(t.key, day, shift, t.position, workerId);
  }

  function rosterCellCoverage(group, day, shift) {
    return rosterSlotFilled(group, day, shift) ? COVERAGE_COLORS.assigned : COVERAGE_COLORS.unassigned;
  }

  function rosterFullyCovered(group) {
    return ROSTER_DAYS.every(d => ['day', 'night'].every(shift => rosterSlotFilled(group, d, shift)));
  }

  // Same tally as rosterFullyCovered, but as a count rather than a single
  // pass/fail — feeds the accordion's collapsed summary for PDIC/RPYCC.
  function rosterCoverageSummary(group) {
    let filled = 0, total = 0;
    for (const d of ROSTER_DAYS) {
      for (const shift of ['day', 'night']) {
        total += 1;
        if (rosterSlotFilled(group, d, shift)) filled += 1;
      }
    }
    return { filled, total };
  }

  // Shared collapsed-state affix for every building card: a small "N/total
  // covered" readout (reusing whichever coverage tally the card already
  // computed) plus a chevron that rotates open. Click handling lives on the
  // card header itself so the whole header row is the toggle target, not
  // just this icon.
  function accordionAffix(buildingId, filled, total) {
    const expanded = expandedBuildings.has(buildingId);
    const allCovered = total > 0 && filled === total;
    const color = allCovered
      ? COVERAGE_TEXT.assigned
      : filled === 0 ? COVERAGE_TEXT.unassigned : COVERAGE_TEXT.understaffed;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
          {allCovered ? `✓ All ${total} covered` : `${filled}/${total} covered`}
        </span>
        <span style={{
          display: 'inline-block', fontSize: 15, color: '#5B6B72', lineHeight: 1,
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s ease',
        }}>▶</span>
      </div>
    );
  }

  // A building's standing day/night roster — the source of truth for who's
  // actually working it, as opposed to the Town's raw submitted windows shown
  // alongside. Kept compact since it lives inside a card rather than having a
  // whole tab to itself.
  function rosterGridFor(group) {
    const hours = rosterHours[group.buildingId];
    return isWide ? (
    <div style={s.rosterGridWrap}>
      <div style={s.rosterGrid}>
        {/* header row */}
        <div style={s.rosterCorner}></div>
        {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
          <div key={d} style={s.rosterDayHead}>
            <div>{d.slice(0, 3).toUpperCase()}</div>
            <div style={s.rosterDayDate}>{dayDate(rentalWeek, d)}</div>
          </div>
        ))}

        {['day', 'night'].map(shift => (
          <Fragment key={shift}>
            <div style={{ ...s.rosterShiftHead, background: NEUTRAL_HEAD }}>
              <div style={s.rosterShiftLabel}>{SHIFT_ACCENTS[shift].icon} {SHIFT_ACCENTS[shift].label}</div>
              <div style={s.rosterShiftTime}>{rosterHoursLong(hours, shift)}</div>
            </div>
            {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => {
              const cov = rosterCellCoverage(group.key, d, shift);
              // The box itself stays on the neutral soft tint regardless of
              // coverage — a shift can hold two different people (e.g. Gary
              // AND Mahmoud), and filling the whole box solid green erased
              // that distinction (caught by Lillian 2026-09-13 on first
              // render). Solid green + white text is per-PERSON only, below.
              return (
                <div key={d} style={{ ...s.rosterCell, background: cov.soft, borderColor: cov.border }}>
                  {Array.from({ length: group.slots ?? 2 }, (_, pos) => pos).map(pos => {
                    const assigned = slotAssigned(group, d, shift, pos);
                    // Only the filled slot carries the green. A blank second
                    // slot stays neutral: a second body is optional here, so
                    // painting it orange would invent a gap that isn't one.
                    const slotFilled = !!assigned?.worker_id;
                    const edge = slotFilled ? COVERAGE_COLORS.assigned.solid : '#8B98A0';
                    return (
                      <select key={pos} style={{
                        ...s.workerSelect, fontSize: 12, padding: '4px 6px', borderLeft: `3px solid ${edge}`,
                        ...(slotFilled ? { background: COVERAGE_COLORS.assigned.solid, color: '#FFFFFF', borderColor: COVERAGE_COLORS.assigned.solid } : {}),
                      }}
                        value={assigned?.worker_id || ''}
                        onChange={(e) => assignSlot(group, d, shift, pos, e.target.value)}>
                        <option value="">{emptySlotLabel(pos)}</option>
                        {workers.map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
                      </select>
                    );
                  })}
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  ) : (
    /* Phone layout: one card per day, stacked */
    <div style={s.rosterStack}>
      {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
        <div key={d} style={s.rosterDayCard}>
          <div style={s.rosterDayCardHead}>{DAY_LABELS[d].toUpperCase()} · {dayDate(rentalWeek, d)}</div>
          {['day', 'night'].map(shift => {
            const cov = rosterCellCoverage(group.key, d, shift);
            // Box stays soft-tinted regardless of coverage — see the wide-grid
            // version above for why (two different people can share a shift).
            return (
              <div key={shift} style={{ ...s.rosterDayCardRow, background: cov.soft, borderRadius: 8 }}>
                <div style={{ ...s.rosterDayTag, background: NEUTRAL_HEAD, color: '#5B6B72', border: '1px solid #E4E0D8' }}>
                  {SHIFT_ACCENTS[shift].icon} {rosterHoursShort(hours, shift)}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {Array.from({ length: group.slots ?? 2 }, (_, pos) => pos).map(pos => {
                    const assigned = slotAssigned(group, d, shift, pos);
                    const slotFilled = !!assigned?.worker_id;
                    const edge = slotFilled ? COVERAGE_COLORS.assigned.solid : '#8B98A0';
                    return (
                      <select key={pos} style={{
                        ...s.workerSelect, fontSize: 12, padding: '4px 6px', borderLeft: `3px solid ${edge}`,
                        ...(slotFilled ? { background: COVERAGE_COLORS.assigned.solid, color: '#FFFFFF', borderColor: COVERAGE_COLORS.assigned.solid } : {}),
                      }}
                        value={assigned?.worker_id || ''}
                        onChange={(e) => assignSlot(group, d, shift, pos, e.target.value)}>
                        <option value="">{emptySlotLabel(pos)}</option>
                        {workers.map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
                      </select>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
    );
  }

  // Generic weekly-schedule building card (used for everything except the
  // two standing-roster cards, which have their own layout).
  function rentalCard(rental) {
    const b = getRentalBuilding(rental.building_id);
    const buildingShort = b?.shortName || b?.name || rental.building_id;
    const assignedCount = (rental.assigned_workers || []).length;
    // Same test the counts and the filter use, so a card can't read green while
    // the week button still says it needs someone.
    const needsStaff = rentalNeedsStaff(rental);
    const understaffed = !needsStaff && rental.min_staff > 1 && assignedCount < rental.min_staff;
    const stripe = needsStaff
      ? COVERAGE_COLORS.unassigned.solid
      : understaffed ? COVERAGE_COLORS.understaffed.solid : COVERAGE_COLORS.assigned.solid;
    const expanded = expandedBuildings.has(rental.building_id);
    const { filled: coverFilled, total: coverTotal } = rentalCoverageSummary(rental);
    return (
      <div key={rental.id} style={{ ...s.buildingCard, borderLeftColor: stripe }}>
        <div
          style={{ ...s.buildingHeader, cursor: 'pointer' }}
          onClick={() => toggleBuildingExpanded(rental.building_id)}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleBuildingExpanded(rental.building_id); }
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ background: 'rgba(31,58,71,0.08)', color: '#1F3A47', borderRadius: 5, padding: '2px 8px', fontSize: 12, fontWeight: 800 }}>
                {rental.building_id}
              </span>
              <span style={s.buildingName}>{buildingShort}</span>
            </div>
            {b?.address && <div style={{ fontSize: 12, color: '#5B6B72', marginBottom: 2 }}>📍 {b.address}</div>}
            <div style={{ fontSize: 12, color: '#5B6B72', marginBottom: 6 }}>
              Min. {rental.min_staff} staff required
            </div>
          </div>
          {accordionAffix(rental.building_id, coverFilled, coverTotal)}
        </div>

        {expanded && (
        <>
        {/* Quick-fill: most buildings have the same person every day, so
            let the manager set all 7 days at once instead of one at a time. */}
        {(rental.windows || []).length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#5B6B72' }}>Fill whole week with:</span>
            <select
              style={{ ...s.workerSelect, fontSize: 12, padding: '4px 8px', width: 'auto' }}
              defaultValue=""
              onChange={(e) => {
                if (!e.target.value) return;
                assignAllWindowsWorker(rental, e.target.value);
                e.target.value = '';
              }}
            >
              <option value="">— choose a worker —</option>
              {workers.map(wk => <option key={wk.id} value={wk.id}>{wk.full_name}</option>)}
            </select>
          </div>
        )}

        {/* Cleaning windows, laid out as one column per day (same grid style
            as the PDIC/RPYCC roster above) instead of a stacked list — each
            slot can pin its own worker for that specific visit (e.g. a
            building cleaned twice a day may need two different people).
            Picking someone here also adds them to the "who can check in"
            pool below automatically. Cells are colored by coverage status
            (green = assigned, orange = still needs someone) since these
            buildings don't have a day/night split to color by instead. */}
        {isWide ? (() => {
          const byDay = ROSTER_DAYS.map(d => (rental.windows || [])
            .map((w, i) => ({ w, i }))
            .filter(({ w }) => w.day === d));
          const maxPerDay = Math.max(1, ...byDay.map(dw => dw.length));
          const shiftRows = maxPerDay > 1 ? [0, 1] : [0];
          return (
            <div style={s.rosterGridWrap}>
              {/* Legend lives once at the top of the list, not in all eleven
                  cards, where it was more repetition than explanation. */}
              <div style={{ ...s.rosterGrid, gridTemplateColumns: 'minmax(60px, 0.6fr) repeat(7, minmax(120px, 1fr))' }}>
                <div style={s.rosterCorner}></div>
                {ROSTER_DAYS.map(d => (
                  <div key={d} style={s.rosterDayHead}>
                    <div>{d.slice(0, 3).toUpperCase()}</div>
                    <div style={s.rosterDayDate}>{dayDate(rentalWeek, d)}</div>
                  </div>
                ))}
                {shiftRows.map(shiftIdx => (
                  <Fragment key={shiftIdx}>
                    <div style={s.rosterShiftHead}>
                      <div style={s.rosterShiftLabel}>{shiftIdx === 0 ? 'FIRST SHIFT' : 'SECOND SHIFT'}</div>
                    </div>
                    {ROSTER_DAYS.map((d, dIdx) => {
                      const dayWindows = byDay[dIdx];
                      const entry = dayWindows[shiftIdx];
                      if (!entry) {
                        return (
                          <div key={d} style={s.rosterCell}>
                            <span style={{ fontSize: 10, color: '#8B98A0', textAlign: 'center' }}>—</span>
                          </div>
                        );
                      }
                      const { w, i } = entry;
                      const wlabel = windowLabel(w, shiftIdx, dayWindows.length);
                      const filled = !!w.worker_id;
                      const cov = filled ? COVERAGE_COLORS.assigned : COVERAGE_COLORS.unassigned;
                      // Box stays soft-tinted either way — only the select
                      // itself (the actual assigned name) goes solid green.
                      return (
                        <div key={d} style={{ ...s.rosterCell, background: cov.soft, borderColor: cov.border }}>
                          <span style={{ fontSize: 11, color: '#1A2B33', textAlign: 'center' }}>{wlabel}</span>
                          <select
                            style={{
                              ...s.workerSelect, fontSize: 11, padding: '3px 4px', borderLeft: `3px solid ${cov.solid}`,
                              ...(filled ? { background: cov.solid, color: '#FFFFFF', borderColor: cov.solid } : {}),
                            }}
                            value={w.worker_id || ''}
                            onChange={(e) => assignRentalWindowWorker(rental, i, e.target.value)}
                          >
                            <option value="">— Assign —</option>
                            {workers.map(wk => <option key={wk.id} value={wk.id}>{wk.full_name}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
              </div>
            </div>
          );
        })() : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 12px 8px' }}>
            {(rental.windows || []).map((w, i) => {
              const sameDay = (rental.windows || []).filter(x => x.day === w.day);
              const idxInDay = sameDay.indexOf(w);
              const wlabel = `${DAY_LABELS[w.day] || w.day} ${dayDate(rentalWeek, w.day)} · ${windowLabel(w, idxInDay, sameDay.length)}`;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 6, padding: '3px 8px', fontSize: 12, color: '#1A2B33', whiteSpace: 'nowrap' }}>
                    {wlabel}
                  </span>
                  <select
                    style={{ ...s.workerSelect, fontSize: 11, padding: '2px 6px', flex: 'none', width: 140 }}
                    value={w.worker_id || ''}
                    onChange={(e) => assignRentalWindowWorker(rental, i, e.target.value)}
                  >
                    <option value="">— Assign —</option>
                    {workers.map(wk => <option key={wk.id} value={wk.id}>{wk.full_name}</option>)}
                  </select>
                </div>
              );
            })}
            {(rental.windows || []).length === 0 && (
              <span style={{ fontSize: 13, color: '#5B6B72' }}>No time slots specified</span>
            )}
          </div>
        )}

        {rental.restrictions && (
          <div style={{ fontSize: 11, color: '#96600A', margin: '0 12px 6px' }}>⚠️ {rental.restrictions}</div>
        )}

        {uniqueWindowNotes(rental).length > 0 && (
          <div style={s.townNotes}>
            <div style={s.townNotesLabel}>FROM THE TOWN THIS WEEK</div>
            {uniqueWindowNotes(rental).map((n, i) => (
              <div key={i} style={s.townNote}>{n}</div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.title}>Schedule</div>
          <div style={s.subtitle}>Upcoming building assignments & special events</div>
        </div>
      </div>

      {/* View toggle */}
      <div style={s.viewToggle}>
        <button style={view === 'rentals' ? { ...s.viewBtn, ...s.viewBtnActive } : s.viewBtn}
          onClick={() => { setView('rentals'); loadRentalSchedules(rentalWeek); }}>📋 Weekly Schedule</button>
        <button style={view === 'events' ? { ...s.viewBtn, ...s.viewBtnActive } : s.viewBtn}
          onClick={() => setView('events')}>⭐ Special Events</button>
      </div>

      {view === 'events' && (
        <>
          {/* Special Events */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '0 12px' }}>
            <div style={s.sectionLabel}>UPCOMING SPECIAL EVENTS</div>
            <button style={s.addBtn} onClick={openAdd}>+ Add Event</button>
          </div>
          {upcomingSpecial.length === 0 ? (
            <div style={s.empty}>No upcoming special events.</div>
          ) : (
            upcomingSpecial.map(event => {
              const days = daysUntil(event.start_date);
              const typeConfig = EVENT_TYPE_LABELS[event.event_type] || EVENT_TYPE_LABELS.special_event;
              return (
                <div key={event.id} style={s.buildingCard}>
                  <div style={s.buildingHeader}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ ...s.typeBadge, background: typeConfig.bg, color: typeConfig.color }}>{typeConfig.label}</span>
                        {/* Actual special-event billing is a flat per-event rate
                            logged separately in the Hours Log/Invoice Generator
                            ($280 short / $520 full-day, not hourly) — this just
                            flags that the event needs those hours logged there. */}
                        {event.hourly_billing && <span style={s.billedBadge}>Log hours →</span>}
                        {days >= 0 && days <= 7 && (
                          <span style={days === 0 ? s.todayChip : s.soonChip}>
                            {days === 0 ? 'TODAY' : days === 1 ? 'TOMORROW' : `IN ${days} DAYS`}
                          </span>
                        )}
                      </div>
                      <div style={s.buildingName}>{event.name}</div>
                      <div style={s.buildingAddr}>📍 {event.location}</div>
                      <div style={{ fontSize: 13, color: '#5B6B72', marginTop: 4 }}>
                        🗓 {formatDateRange(event.start_date, event.end_date)}
                      </div>
                    </div>
                    <div style={s.buildingActions}>
                      <button style={s.editBtn} onClick={() => openEdit(event)}>Edit</button>
                      <button style={s.deleteBtn} onClick={() => handleDelete(event.id)}>✕</button>
                    </div>
                  </div>

                  {/* Details submitted via Town Portal */}
                  {event.details && (event.details.description || event.details.time_start || event.details.setup_requirements || event.details.cleanup_requirements || event.details.estimated_attendance) && (
                    <div style={s.detailsBox}>
                      <div style={s.detailsFrom}>From Town Portal</div>
                      {event.details.time_start && (
                        <div style={s.detailRow}>
                          <span style={s.detailKey}>Time</span>
                          <span>{formatTime(event.details.time_start)}{event.details.time_end ? ` – ${formatTime(event.details.time_end)}` : ''}</span>
                        </div>
                      )}
                      {event.details.description && (
                        <div style={s.detailRow}>
                          <span style={s.detailKey}>Description</span>
                          <span>{event.details.description}</span>
                        </div>
                      )}
                      {event.details.estimated_attendance && (
                        <div style={s.detailRow}>
                          <span style={s.detailKey}>Attendance</span>
                          <span>~{event.details.estimated_attendance} people</span>
                        </div>
                      )}
                      {event.details.setup_requirements && (
                        <div style={s.detailRow}>
                          <span style={s.detailKey}>Setup</span>
                          <span>{event.details.setup_requirements}</span>
                        </div>
                      )}
                      {event.details.cleanup_requirements && (
                        <div style={s.detailRow}>
                          <span style={s.detailKey}>Cleanup</span>
                          <span>{event.details.cleanup_requirements}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {event.notes && <div style={s.buildingNotes}>{event.notes}</div>}

                  {/* Worker assignment for special events — multi-select */}
                  <div style={s.assignSection}>
                    <div style={s.assignLabel}>Assigned Workers:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {workers.map(w => {
                        const isAssigned = (event.assigned_workers || []).includes(w.id);
                        return (
                          <button key={w.id}
                            style={isAssigned
                              ? { ...s.workerChip, background: 'rgba(31,58,71,0.08)', borderColor: '#1F3A47', color: '#1F3A47' }
                              : s.workerChip}
                            onClick={() => {
                              const newWorkers = isAssigned
                                ? event.assigned_workers.filter(id => id !== w.id)
                                : [...(event.assigned_workers || []), w.id];
                              const newNames = isAssigned
                                ? (event.assigned_worker_names || []).filter(n => n !== w.full_name)
                                : [...(event.assigned_worker_names || []), w.full_name];
                              supabase.from('scheduled_events').update({
                                assigned_workers: newWorkers,
                                assigned_worker_names: newNames,
                              }).eq('id', event.id).then(() => reloadData());
                            }}>
                            {isAssigned ? '✓ ' : ''}{w.full_name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

      {view === 'rentals' && (
        <>
          <div style={s.sectionLabel}>WEEKLY BUILDING SCHEDULE — SUBMITTED BY TOWN OF PARADISE</div>

          {/* Week selector. This week and next week are one tap each, with the
              amount of work outstanding on the button, because the week being
              edited was previously invisible until you opened the dropdown —
              and next week could sit fully unassigned without anything on
              screen saying so. Both weeks are named rather than defaulting
              forward: the same page is used mid-week to move people around
              today, and quietly opening on next week makes editing the wrong
              week easy and silent. */}
          <div style={{ padding: '0 12px 12px' }}>
            <div style={s.weekTabRow}>
              {getUpcomingMondays(2).map((wk, idx) => {
                const active = rentalWeek === wk;
                const sum = weekSummary(wk);
                let status = null;
                if (sum) {
                  if (sum.submitted === 0) status = { text: 'Not submitted yet', color: '#5B6B72' };
                  else if (sum.needing === 0) status = { text: '✓ Everyone assigned', color: COVERAGE_TEXT.assigned };
                  else status = { text: `${sum.needing} to assign`, color: COVERAGE_TEXT.unassigned };
                }
                return (
                  <button
                    key={wk}
                    style={{ ...s.weekTab, ...(active ? s.weekTabActive : {}) }}
                    onClick={() => selectWeek(wk)}
                  >
                    <div style={s.weekTabTitle}>{idx === 0 ? 'This week' : 'Next week'}</div>
                    <div style={s.weekTabDates}>{formatWeekLabel(wk)}</div>
                    {status && <div style={{ ...s.weekTabStatus, color: status.color }}>{status.text}</div>}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
              <label style={s.otherWeekLabel} htmlFor="other-week">Another week</label>
              <select
                id="other-week"
                style={{ ...s.workerSelect, fontSize: 13, width: 'auto' }}
                value={rentalWeek}
                onChange={e => selectWeek(e.target.value)}
              >
                {getUpcomingMondays(8).map(m => (
                  <option key={m} value={m}>{formatWeekLabel(m)}</option>
                ))}
              </select>
              <button
                style={{ ...s.addBtn, width: 'auto', opacity: copyingLastWeek || loadingRentals ? 0.6 : 1 }}
                disabled={copyingLastWeek || loadingRentals}
                onClick={copyPreviousWeekAssignments}
                title="Fills any still-blank worker slots using who worked that day/visit the week before. Never overwrites a slot you've already assigned."
              >
                {copyingLastWeek ? 'Copying…' : `📋 Copy from ${formatWeekLabel(prevMonday(rentalWeek))}`}
              </button>
            </div>
          </div>

          {/* Stays in view while scrolling: the cards are tall enough that a
              few buildings down there was nothing left on screen saying which
              week was being edited. Also the one place that says the work
              saves itself, and explains the stripe colours — both of which
              used to be repeated in every card, or missing entirely. */}
          {!loadingRentals && (
            <div style={s.stickyBar}>
              <div style={s.stickyRow}>
                <span style={s.stickyWeek}>
                  {rentalWeek === getUpcomingMondays(1)[0] ? 'This week' : rentalWeek === getUpcomingMondays(2)[1] ? 'Next week' : 'Week of'}
                  {' · '}{formatWeekLabel(rentalWeek)}
                </span>
                {needingCount > 0 && (
                  <button
                    style={{ ...s.filterBtn, ...(onlyNeeding ? s.filterBtnActive : {}) }}
                    onClick={() => setOnlyNeeding(v => !v)}
                  >
                    {onlyNeeding ? '✓ Showing only what needs staff' : `Show only what needs staff (${needingCount})`}
                  </button>
                )}
              </div>
              <div style={s.legendRow}>
                <span style={{ ...s.legendChip, background: COVERAGE_COLORS.assigned.solid, borderColor: COVERAGE_COLORS.assigned.solid, color: '#FFFFFF' }}>● Fully assigned</span>
                <span style={{ ...s.legendChip, background: COVERAGE_COLORS.unassigned.soft, borderColor: COVERAGE_COLORS.unassigned.border, color: COVERAGE_TEXT.unassigned }}>● Needs attention</span>
                <span style={s.autosaveNote}>Choices save as you make them — there's no Save button.</span>
              </div>
            </div>
          )}

          {copyResult && (
            <div style={{ margin: '0 12px 12px', background: 'rgba(47,122,79,0.08)', border: '1px solid #2F7A4F', borderRadius: 8, padding: '10px 12px', color: '#2F7A4F', fontSize: 12.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span>✓ {copyResult}</span>
              <button style={{ background: 'none', border: 'none', color: '#2F7A4F', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }} onClick={() => setCopyResult(null)}>Dismiss</button>
            </div>
          )}

          {loadingRentals ? (
            <div style={s.empty}>Loading...</div>
          ) : (() => {
            // The arena and the youth centre each get their own card, in normal
            // building order rather than pinned above everything. They're shown
            // even when the Town hasn't submitted a row for them this week:
            // both are standing 7-day contracts rather than week-to-week.
            //
            // They shared one card until Aug 2026, on the reasoning that they
            // share staff. They don't — Gary and Mahmoud work the arena while
            // April works the youth centre — and merging them meant a night
            // needing three people had only two slots to say so.
            const rowFor = (id) => rentalSchedules.find(r => r.building_id === id);

            // One combined card for E and F: workers check in once at E, so the
            // two standing rosters sit together, each keeping its own slots.
            const rosterCard = () => {
              const g = EF_GROUP;
              const notes = [...uniqueWindowNotes(rowFor('E')), ...uniqueWindowNotes(rowFor('F'))];
              const hours = rosterHours[g.buildingId];
              const covered = rosterFullyCovered('EF');
              const expanded = expandedBuildings.has(g.buildingId);
              const { filled: coverFilled, total: coverTotal } = rosterCoverageSummary('EF');
              return (
                <div key="EF" style={{ ...s.buildingCard, borderLeftColor: covered ? COVERAGE_COLORS.assigned.solid : COVERAGE_COLORS.unassigned.solid }}>
                  <div
                    style={{ ...s.buildingHeader, cursor: 'pointer' }}
                    onClick={() => toggleBuildingExpanded(g.buildingId)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleBuildingExpanded(g.buildingId); }
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ background: 'rgba(31,58,71,0.08)', color: '#1F3A47', borderRadius: 5, padding: '2px 8px', fontSize: 12, fontWeight: 800 }}>{g.code}</span>
                        <span style={s.buildingName}>{g.name}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#5B6B72', marginBottom: 2 }}>📍 1 Sarah Davis Way · 7 days/week · LEED</div>
                    </div>
                    {accordionAffix(g.buildingId, coverFilled, coverTotal)}
                  </div>

                  {expanded && (
                  <>
                  <div style={s.legendRow}>
                    <span style={{ ...s.legendChip, background: NEUTRAL_HEAD, borderColor: '#E4E0D8', color: '#5B6B72' }}>{SHIFT_ACCENTS.day.icon} {rosterHoursShort(hours, 'day')}</span>
                    <span style={{ ...s.legendChip, background: NEUTRAL_HEAD, borderColor: '#E4E0D8', color: '#5B6B72' }}>{SHIFT_ACCENTS.night.icon} {rosterHoursShort(hours, 'night')}</span>
                  </div>
                  {rosterError && (
                    <div style={{ margin: '10px 0', background: '#FBF1EF', border: '1px solid #B33A2E', borderRadius: 8, padding: '10px 12px', color: '#B33A2E', fontSize: 12.5 }}>
                      ⚠ {rosterError}
                    </div>
                  )}
                  {rosterGridFor(g)}
                  <div style={{ fontSize: 12, color: '#5B6B72', marginTop: 4, fontStyle: 'italic' }}>⚠️ Tuesdays: No boardroom/kitchen at the youth centre during council meetings 3:30–10pm</div>
                  <div style={{ fontSize: 11, color: '#5B6B72', marginTop: 4 }}>
                    Tap any box to change. Saved automatically. This roster repeats every week — changing it here changes it for all weeks, not just this one.
                  </div>
                  {notes.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ fontSize: 11, color: '#5B6B72', fontWeight: 700, letterSpacing: 0.5 }}>TOWN-SUBMITTED COVERAGE NOTES</div>
                      {notes.map((n, i) => <div key={i} style={{ fontSize: 12, color: '#5B6B72' }}>{n}</div>)}
                    </div>
                  )}
                  </>
                  )}
                </div>
              );
            };

            const rosterCards = [rosterCard()];

            const allOther = rentalSchedules.filter(r => r.building_id !== 'E' && r.building_id !== 'F');
            // While filtering, both roster cards drop out with everything else
            // that's covered — they're staffed by the standing roster, so they
            // are never part of what's left to do for a given week.
            const otherRentals = onlyNeeding ? allOther.filter(rentalNeedsStaff) : allOther;

            if (onlyNeeding && otherRentals.length === 0) {
              return (
                <div style={s.empty}>
                  Every building for this week has someone on it.
                  <div style={{ marginTop: 8 }}>
                    <button style={s.filterBtn} onClick={() => setOnlyNeeding(false)}>Show all buildings</button>
                  </div>
                </div>
              );
            }

            const cards = [];
            let inserted = false;
            for (const rental of otherRentals) {
              if (!onlyNeeding && !inserted && rental.building_id > 'F') { cards.push(...rosterCards); inserted = true; }
              cards.push(rentalCard(rental));
            }
            if (!onlyNeeding && !inserted) cards.push(...rosterCards);

            if (otherRentals.length === 0 && !rowFor('E') && !rowFor('F')) {
              return (
                <div style={s.empty}>
                  No weekly schedule submitted by the Town for this week.
                  <div style={{ marginTop: 8, fontSize: 12, color: '#5B6B72' }}>
                    Jason Lawrence submits schedules at the Town Portal login.
                  </div>
                </div>
              );
            }
            return cards;
          })()}
        </>
      )}

      {/* Add / Edit Form Modal */}
      {showAddForm && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{editingEvent ? 'Edit Entry' : 'Add Event'}</div>
              <button style={s.closeBtn} onClick={() => setShowAddForm(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <label style={s.label}>TYPE</label>
              <div style={s.typeRow}>
                {/* "Weekly Clean" (rental) intentionally excluded here — that
                    used to create scheduled_events rows with nowhere left to
                    display (see removed "Other Buildings" section); use the
                    Weekly Schedule tab for recurring building cleanings now. */}
                {Object.entries(EVENT_TYPE_LABELS).filter(([type]) => type !== 'rental').map(([type, config]) => (
                  <button key={type}
                    style={form.event_type === type
                      ? { ...s.typeBtn, background: config.bg, border: `1px solid ${config.color}`, color: config.color }
                      : s.typeBtn}
                    onClick={() => setForm(prev => ({ ...prev, event_type: type }))}>
                    {config.label}
                  </button>
                ))}
              </div>

              <label style={s.label}>NAME *</label>
              <input style={s.input} placeholder="e.g. Easter Brunch"
                value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />

              <label style={s.label}>BUILDING</label>
              <select style={s.input} value={form.building_id}
                onChange={e => {
                  const id = e.target.value;
                  const b = BUILDINGS.find(x => x.id === id);
                  setForm(p => ({
                    ...p,
                    building_id: id,
                    // Only auto-fill location if it's empty or was previously auto-filled —
                    // don't clobber a custom location the manager already typed.
                    location: (!p.location || BUILDINGS.some(x => `${x.shortName || x.name}`.trim() === p.location.trim()))
                      ? (b ? (b.shortName || b.name) : p.location)
                      : p.location,
                  }));
                }}>
                <option value="">— No specific building (custom location) —</option>
                {BUILDINGS.filter(b => b.status !== 'special').map(b => (
                  <option key={b.id} value={b.id}>{b.id} — {b.shortName || b.name}</option>
                ))}
              </select>

              <label style={s.label}>LOCATION *</label>
              <input style={s.input} placeholder="e.g. RPYCC Gym"
                value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} />

              <label style={s.label}>START DATE *</label>
              <input style={s.input} type="date"
                value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value, end_date: p.end_date || e.target.value }))} />

              <label style={s.label}>END DATE</label>
              <input style={s.input} type="date"
                value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />

              <label style={s.label}>NOTES</label>
              <textarea style={s.textarea} rows={3} placeholder="Times, instructions, staffing notes..."
                value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />

              <label style={s.label}>ASSIGN WORKERS</label>
              <div style={s.workerList}>
                {workers.map(w => (
                  <button key={w.id}
                    style={form.assigned_workers.includes(w.id)
                      ? { ...s.workerBtn, background: 'rgba(31,58,71,0.08)', border: '1px solid #1F3A47', color: '#1F3A47' }
                      : s.workerBtn}
                    onClick={() => toggleWorker(w)}>
                    {form.assigned_workers.includes(w.id) ? '✓ ' : ''}{w.full_name}
                  </button>
                ))}
              </div>
            </div>
            {saveError && (
              <div style={{ margin: '0 20px 12px', background: '#FBF1EF', border: '1px solid #B33A2E', borderRadius: 8, padding: '10px 12px', color: '#B33A2E', fontSize: 12.5 }}>
                Could not save: {saveError}
              </div>
            )}
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowAddForm(false)}>Cancel</button>
              <button
                style={(!form.name || !form.location || !form.start_date || saving) ? { ...s.saveBtn, opacity: 0.5 } : s.saveBtn}
                disabled={!form.name || !form.location || !form.start_date || saving}
                onClick={handleSave}>
                {saving ? 'Saving...' : editingEvent ? 'Save Changes' : 'Add to Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '0 0 40px', color: '#1A2B33', background: '#F5F3EF' },
  loading: { padding: 40, textAlign: 'center', color: '#5B6B72' },
  empty: { padding: 24, textAlign: 'center', color: '#5B6B72', fontSize: 14, border: '1px dashed #E4E0D8', borderRadius: 10, margin: '0 12px' },

  detailsBox: { background: '#F5F3EF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '10px 12px', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 5 },
  detailsFrom: { fontSize: 9, fontWeight: 800, letterSpacing: '1.5px', color: '#1F3A47', textTransform: 'uppercase', marginBottom: 4 },
  detailRow: { display: 'flex', gap: 10, fontSize: 12, color: '#5B6B72', alignItems: 'flex-start' },
  detailKey: { fontSize: 10, fontWeight: 700, color: '#5B6B72', textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: 76, flexShrink: 0, paddingTop: 1 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 12px', flexWrap: 'wrap', rowGap: 10 },
  title: { fontSize: 22, fontWeight: 800, color: '#1A2B33', letterSpacing: '-0.5px' },
  subtitle: { fontSize: 13, color: '#5B6B72', marginTop: 2 },
  // A real action, but not a status — kept off the orange the coverage
  // legend uses, so clicking "Add" never visually reads as a warning.
  addBtn: { background: '#1F3A47', color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  weekTabRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  weekTab: {
    flex: '1 1 180px', textAlign: 'left', cursor: 'pointer',
    background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 10, padding: '10px 12px',
  },
  // Being the selected week is a navigation state, not a coverage problem —
  // one quiet, consistent "this is chosen" navy, reused for every active
  // control on this page (week tab, filter, view toggle), kept apart from
  // the coverage legend's green/amber/orange.
  weekTabActive: { background: 'rgba(31,58,71,0.08)', borderColor: '#1F3A47' },
  weekTabTitle: { color: '#1A2B33', fontSize: 14, fontWeight: 800 },
  weekTabDates: { color: '#5B6B72', fontSize: 12, marginTop: 1 },
  weekTabStatus: { fontSize: 12, fontWeight: 700, marginTop: 4 },
  otherWeekLabel: { fontSize: 12, color: '#5B6B72' },
  stickyBar: {
    position: 'sticky', top: 0, zIndex: 5,
    background: '#FFFFFF', borderTop: '1px solid #E4E0D8', borderBottom: '1px solid #E4E0D8',
    padding: '8px 12px', margin: '0 0 10px',
  },
  stickyRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  stickyWeek: { color: '#1A2B33', fontSize: 13, fontWeight: 700 },
  filterBtn: {
    background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72',
    borderRadius: 999, padding: '5px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  filterBtnActive: { background: 'rgba(31,58,71,0.08)', borderColor: '#1F3A47', color: '#1F3A47' },
  autosaveNote: { fontSize: 11.5, color: '#5B6B72' },

  townNotes: {
    margin: '0 12px 10px', padding: '8px 11px',
    background: 'rgba(181,115,10,0.08)', border: '1px solid rgba(181,115,10,0.35)',
    borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 4,
  },
  townNotesLabel: { fontSize: 10, fontWeight: 800, letterSpacing: 1, color: '#96600A' },
  townNote: { fontSize: 12.5, color: '#1A2B33', lineHeight: 1.45 },

  viewToggle: { display: 'flex', gap: 6, padding: '0 12px 16px' },
  viewBtn: { flex: 1, background: '#FFFFFF', color: '#5B6B72', border: '1px solid #E4E0D8', borderRadius: 10, padding: '10px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', textAlign: 'center' },
  viewBtnActive: { background: 'rgba(31,58,71,0.08)', color: '#1F3A47', borderColor: '#1F3A47' },

  sectionLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: '#5B6B72', padding: '8px 20px 8px', textTransform: 'uppercase' },

  legendRow: { display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 16px 12px' },
  legendChip: { fontSize: 11, fontWeight: 700, border: '1px solid', borderRadius: 6, padding: '4px 10px', letterSpacing: 0.3 },

  buildingCard: { margin: '0 12px 10px', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 12, padding: 16, borderLeft: '3px solid #E4E0D8' },
  buildingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  buildingName: { fontSize: 16, fontWeight: 700, color: '#1A2B33' },
  buildingAddr: { fontSize: 12, color: '#5B6B72', marginTop: 2 },
  buildingNotes: { fontSize: 12, color: '#5B6B72', lineHeight: 1.6, background: '#F5F3EF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '10px 12px', marginBottom: 10 },
  buildingActions: { display: 'flex', gap: 6, flexShrink: 0 },

  // Shift grid for PDIC — currently unused (superseded by rosterGridFor
  // below) but kept in the same light palette rather than left stale.
  shiftGrid: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 },
  shiftCard: { padding: '12px 14px', background: '#F5F3EF', borderRadius: 10, border: '1px solid #E4E0D8' },
  shiftRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#F5F3EF', borderRadius: 8 },
  shiftDot: { width: 10, height: 10, borderRadius: 5, flexShrink: 0 },
  shiftInfo: { flex: 1, minWidth: 0 },
  shiftLabel: { fontSize: 13, fontWeight: 700, color: '#1A2B33' },
  shiftTime: { fontSize: 11, color: '#5B6B72' },
  shiftWorker: { flexShrink: 0 },
  assignedName: { fontSize: 12, fontWeight: 600, color: '#2F7A4F', background: 'rgba(47,122,79,0.10)', padding: '3px 8px', borderRadius: 6 },
  unassignedText: { fontSize: 12, color: '#B33A2E', fontWeight: 600 },
  shiftNote: { fontSize: 11, color: '#5B6B72', marginTop: 4, fontStyle: 'italic' },

  // Worker assignment
  assignSection: { marginTop: 10, borderTop: '1px solid #E4E0D8', paddingTop: 10 },
  assignLabel: { fontSize: 11, fontWeight: 700, color: '#5B6B72', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  workerSelect: { width: '100%', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '10px 12px', color: '#1A2B33', fontSize: 14, outline: 'none', appearance: 'auto' },
  workerChip: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },

  // Weekly roster grid (PDIC/RPYCC)
  rosterGridWrap: { overflowX: 'auto', marginBottom: 8 },
  rosterGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(70px, 0.9fr) repeat(7, minmax(96px, 1fr))',
    gap: 4,
    minWidth: 720,
  },
  rosterCorner: { background: '#F5F3EF' },
  rosterDayHead: {
    background: '#F5F3EF', color: '#5B6B72', fontSize: 11, fontWeight: 800,
    textAlign: 'center', padding: '8px 4px', borderBottom: '1px solid #E4E0D8', letterSpacing: 0.5,
  },
  rosterDayDate: { fontSize: 11, fontWeight: 700, color: '#1A2B33', letterSpacing: 0, marginTop: 2 },
  rosterShiftHead: {
    padding: '8px 10px', color: '#1A2B33', display: 'flex', flexDirection: 'column', justifyContent: 'center',
    borderRight: '1px solid #E4E0D8', borderRadius: 6,
  },
  rosterShiftLabel: { fontSize: 12, fontWeight: 800, letterSpacing: 0.5 },
  rosterShiftTime: { fontSize: 10, color: '#5B6B72', marginTop: 2 },
  rosterCell: {
    background: '#F5F3EF', borderRadius: 6, padding: 4, display: 'flex', flexDirection: 'column', gap: 4,
    border: '1px solid #E4E0D8',
  },

  // Phone stacked layout (per-day cards)
  rosterStack: { display: 'flex', flexDirection: 'column', gap: 8 },
  rosterDayCard: {
    background: '#FFFFFF', borderRadius: 10, border: '1px solid #E4E0D8', padding: 10,
  },
  rosterDayCardHead: {
    fontSize: 13, fontWeight: 800, color: '#1A2B33', marginBottom: 8,
    borderBottom: '1px solid #E4E0D8', paddingBottom: 6,
  },
  rosterDayCardRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'stretch' },
  rosterDayTag: {
    width: 78, flexShrink: 0, borderRadius: 6, fontSize: 10, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 4px',
  },

  // Event badges
  typeBadge: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4 },
  billedBadge: { fontSize: 11, fontWeight: 700, color: '#2F7A4F', background: 'rgba(47,122,79,0.10)', padding: '3px 8px', borderRadius: 4 },
  todayChip: { background: '#1F3A47', color: '#FFFFFF', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 4 },
  soonChip: { background: 'rgba(31,58,71,0.08)', color: '#1F3A47', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 },

  // Edit/delete buttons
  editBtn: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  deleteBtn: { background: 'transparent', border: 'none', color: '#8B98A0', cursor: 'pointer', fontSize: 14, padding: '4px 6px' },

  // Modal — the one legitimate floating element on this page, so it's also
  // the one place on this screen that keeps a drop shadow.
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modal: { background: '#FFFFFF', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 30px rgba(26,43,51,0.18)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 12px', borderBottom: '1px solid #E4E0D8' },
  modalTitle: { fontSize: 17, fontWeight: 700, color: '#1A2B33' },
  closeBtn: { background: 'none', border: 'none', color: '#5B6B72', fontSize: 20, cursor: 'pointer' },
  modalBody: { padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 },
  modalFooter: { padding: '16px 20px', borderTop: '1px solid #E4E0D8', display: 'flex', gap: 10 },
  label: { fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: '#5B6B72', display: 'block', marginBottom: 6 },
  input: { width: '100%', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '12px 14px', color: '#1A2B33', fontSize: 15, outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 8, padding: '12px 14px', color: '#1A2B33', fontSize: 14, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  typeRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  typeBtn: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  workerList: { display: 'flex', flexDirection: 'column', gap: 6 },
  workerBtn: { background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 8, padding: '10px 14px', fontSize: 14, fontWeight: 500, cursor: 'pointer', textAlign: 'left' },
  cancelBtn: { flex: 1, background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72', borderRadius: 8, padding: '12px 0', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  saveBtn: { flex: 2, background: '#1F3A47', border: 'none', color: '#FFFFFF', borderRadius: 8, padding: '12px 0', fontWeight: 800, fontSize: 14, cursor: 'pointer' },
};